import { Box, Button, CloseButton, Flex, Stack, Text } from '@mantine/core';
import { FieldValues, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IssnCategorySelectControl } from '@/module/Journal/components/form/IssnCategorySelectControl';

import { FormControlInput, TextInputControl } from '@/core/components';

import { issnCategoryList } from '../../model/JournalTypes';

export interface IssnListControlProps<T extends FieldValues> extends Omit<FormControlInput<T>, 'name'> {
  withAsterisk?: boolean;

  name: string;

  label: string;

  description?: string;
  isDisabled?: boolean;
}

const defaultIssnList = {
  code: '',
  category: 'PRINT',
};

export function IssnListControl<T extends FieldValues>(props: IssnListControlProps<T>) {
  const { label, description, isSubmitting, isDisabled, name } = props;
  const { fields, append, remove } = useFieldArray({ name, keyName: 'uuid' });
  const { t } = useTranslation();
  defaultIssnList.code = '';
  defaultIssnList.category = '';

  return (
    <Box>
      <Flex justify="space-between">
        <Box>
          <Text
            fw="500"
            size="xl"
          >
            {label}
          </Text>
          {description && <Text c="dimmed">{description}</Text>}
        </Box>
        <Button
          disabled={isDisabled}
          onClick={() => {
            defaultIssnList.code = '';
            defaultIssnList.category = String(issnCategoryList.at(fields.length < 3 ? fields.length : 0));
            append(defaultIssnList);
          }}
        >
          {t('button.add')}
        </Button>
      </Flex>
      <Stack gap="xl">
        {fields.length === 0 && (
          <Text
            size="xs"
            c="dimmed"
            fs="italic"
          >
            {t('journals.form.fields.emptyIssnList')}
          </Text>
        )}
        {fields.map((field, index) => (
          <Flex
            align="center"
            justify="center"
            gap="4px"
            key={field.uuid}
          >
            <Text
              mr="8px"
              mt="25px"
              size="sm"
            >
              #{index + 1}
            </Text>
            <TextInputControl
              label={t('journals.form.fields.code')}
              name={`${name}.${index}.code`}
              isSubmitting={isSubmitting}
              isDisabled={isDisabled}
            />
            <IssnCategorySelectControl
              label={t('journals.form.fields.type')}
              name={`${name}.${index}.category`}
              isSubmitting={isSubmitting}
              isDisabled={isDisabled}
            />
            <CloseButton
              mt="25px"
              onClick={() => remove(index)}
              c="red"
              aria-label={t('journals.form.fields.removeIssn')}
            />
          </Flex>
        ))}
      </Stack>
    </Box>
  );
}
